import type { AutomationStep, FailureHandlerStep } from '@/types/automationSteps.ts';
import { ErrorBoundaryRecoveryPolicy } from '@/types/automationSteps.ts';
import { InputSource, StepType } from '@/types/automationFields.ts';
import { getAllStepChildren, isStepWithChildren } from '@/utils/stepUtils.ts';

export type ContinuationRoutingWarning = {
  boundaryStepId: string;
  unverifiedConsumerStepIds: string[];
};

/**
 * Finds configured uses of an outcome-specific continuation output that are
 * not nested under a Conditional branch which takes the boundary receipt as
 * an input. This is deliberately structural: it cannot establish whether a
 * condition evaluates the receipt outcome correctly at runtime.
 */
export function findContinuationRoutingWarnings(steps: AutomationStep[]): ContinuationRoutingWarning[] {
  const boundaries = flattenSteps(steps).filter(
    (step): step is FailureHandlerStep =>
      step.step_type === StepType.FailureHandler &&
      step.recovery_policy === ErrorBoundaryRecoveryPolicy.ContinueAfterRecovery &&
      !!step.recovery_receipt_output &&
      (step.recovery_continuation_outputs?.length ?? 0) + (step.protected_success_outputs?.length ?? 0) > 0,
  );

  return boundaries.flatMap((boundary) => {
    const receiptOutputId = boundary.recovery_receipt_output?.output_id;
    if (!receiptOutputId) return [];

    const continuationOutputIds = new Set([
      ...(boundary.protected_success_outputs ?? []).map((output) => output.output_id),
      ...(boundary.recovery_continuation_outputs ?? []).map((output) => output.output_id),
    ]);
    const unverifiedConsumerStepIds = new Set<string>();

    for (const root of steps) {
      collectUngatedConsumers({
        step: root,
        boundaryStepId: boundary.step_id,
        continuationOutputIds,
        receiptOutputId,
        receiptRouted: false,
        unverifiedConsumerStepIds,
      });
    }

    return unverifiedConsumerStepIds.size
      ? [{ boundaryStepId: boundary.step_id, unverifiedConsumerStepIds: [...unverifiedConsumerStepIds] }]
      : [];
  });
}

function collectUngatedConsumers({
  step,
  boundaryStepId,
  continuationOutputIds,
  receiptOutputId,
  receiptRouted,
  unverifiedConsumerStepIds,
}: {
  step: AutomationStep;
  boundaryStepId: string;
  continuationOutputIds: Set<string>;
  receiptOutputId: string;
  receiptRouted: boolean;
  unverifiedConsumerStepIds: Set<string>;
}) {
  if (step.step_type === StepType.Conditional) {
    if (referencesBoundaryOutput(step, boundaryStepId, continuationOutputIds, new Set(['conditions']))) {
      if (!receiptRouted) unverifiedConsumerStepIds.add(step.step_id);
    }
    let receiptRoutedByPreviousCondition = receiptRouted;
    for (const condition of step.conditions) {
      const conditionUsesContinuationOutput = referencesBoundaryOutput(
        condition,
        boundaryStepId,
        continuationOutputIds,
        new Set(['children']),
      );
      if (conditionUsesContinuationOutput && !receiptRoutedByPreviousCondition) {
        unverifiedConsumerStepIds.add(step.step_id);
      }

      const conditionUsesReceipt = conditionReferencesReceipt(condition, boundaryStepId, receiptOutputId);
      for (const child of condition.children) {
        collectUngatedConsumers({
          step: child,
          boundaryStepId,
          continuationOutputIds,
          receiptOutputId,
          receiptRouted: receiptRoutedByPreviousCondition || conditionUsesReceipt,
          unverifiedConsumerStepIds,
        });
      }
      receiptRoutedByPreviousCondition ||= conditionUsesReceipt;
    }
    return;
  }

  if (
    !receiptRouted &&
    referencesBoundaryOutput(step, boundaryStepId, continuationOutputIds, new Set(['children', 'handler_children']))
  ) {
    unverifiedConsumerStepIds.add(step.step_id);
  }

  if (isStepWithChildren(step)) {
    for (const child of getAllStepChildren(step)) {
      collectUngatedConsumers({
        step: child,
        boundaryStepId,
        continuationOutputIds,
        receiptOutputId,
        receiptRouted,
        unverifiedConsumerStepIds,
      });
    }
  }
}

function conditionReferencesReceipt(
  condition: { condition_data_inputs?: unknown; condition_logic_source?: InputSource; condition_logic_data_inputs?: unknown },
  boundaryStepId: string,
  receiptOutputId: string,
): boolean {
  const receiptOutputIds = new Set([receiptOutputId]);
  if (referencesBoundaryOutput(condition.condition_data_inputs, boundaryStepId, receiptOutputIds, new Set())) {
    return true;
  }
  return (
    (condition.condition_logic_source === InputSource.FromDataset || condition.condition_logic_source === InputSource.Both) &&
    referencesBoundaryOutput(condition.condition_logic_data_inputs, boundaryStepId, receiptOutputIds, new Set())
  );
}

function referencesBoundaryOutput(
  value: unknown,
  boundaryStepId: string,
  outputIds: Set<string>,
  ignoredKeys: Set<string>,
): boolean {
  if (Array.isArray(value)) {
    return value.some((item) => referencesBoundaryOutput(item, boundaryStepId, outputIds, ignoredKeys));
  }
  if (!value || typeof value !== 'object') return false;

  const record = value as Record<string, unknown>;
  const config = record.config;
  if (
    record.data_input_type === 'dynamic' &&
    config &&
    typeof config === 'object' &&
    (config as Record<string, unknown>).source_step_id === boundaryStepId &&
    outputIds.has(String((config as Record<string, unknown>).output_id || ''))
  ) {
    return true;
  }

  return Object.entries(record).some(
    ([key, nested]) => !ignoredKeys.has(key) && referencesBoundaryOutput(nested, boundaryStepId, outputIds, ignoredKeys),
  );
}

function flattenSteps(steps: AutomationStep[]): AutomationStep[] {
  return steps.flatMap((step) => [
    step,
    ...(isStepWithChildren(step) ? flattenSteps(getAllStepChildren(step)) : []),
  ]);
}
